import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ApiError } from '~/api/errors';
import { queryKeys } from '~/api/queryKeys';
import { extractApiError } from '~/utils/extract-api-error';

import {
  createExperimentDraft,
  deleteExperimentDraft,
  deleteExperimentOfferIds,
  fetchExperimentDraft,
  updateExperimentDraft,
  uploadExperimentOfferIds,
} from '../services/experiment-draft.service';
import type { CandidateIdType } from '../types/create-experiment.types';
import type { DraftExperimentUpdateBody } from '../types/experiment-draft.types';

/**
 * Mutation for creating a draft experiment on "Save Grouping & Continue".
 * Sends `parentIdField` and `adPlatform` as query params so the backend can
 * scope the draft to the right ad platform; the request body is empty.
 * Surfaces an error toast only — success communicated via the wizard
 * advancing.
 */
export function useCreateExperimentDraft() {
  return useMutation({
    mutationFn: (args: {
      parentIdField: string;
      adPlatform: string;
      armAssignmentFile?: File;
      offerIdsFile?: File;
      offerIdsType?: CandidateIdType;
    }) => createExperimentDraft(args),
    onError: (err) => {
      toast.error(extractApiError(err, 'Failed to create experiment draft. Please try again.'));
    },
  });
}

/**
 * Candidate-scope upload + clear, as one hook: the two actions sit on the same
 * control and share an invalidation target.
 *
 * Deliberately raises no error toast. Every documented failure (400 on the
 * file's shape, 404 on the experiment, 409 once content scoring has consumed the
 * pool) belongs against the file field itself, so the caller reads `error` and
 * places the message. Success updates the draft cache in place from the returned
 * DTO — the response carries the fresh `offerIds*` counts, so there is nothing
 * to refetch.
 */
export function useExperimentOfferIds(experimentId: string | null) {
  const { t } = useTranslation('experiments');
  const queryClient = useQueryClient();

  const applyResult = (draft: Awaited<ReturnType<typeof uploadExperimentOfferIds>>) => {
    if (!experimentId) return;
    queryClient.setQueryData(queryKeys.experiments.draft(experimentId), draft);
  };

  const upload = useMutation({
    mutationFn: ({ file, idType }: { file: File; idType: CandidateIdType }) =>
      uploadExperimentOfferIds(experimentId ?? '', file, idType),
    onSuccess: (draft) => {
      applyResult(draft);
      toast.success(t('create.step2.upload.uploaded', { count: draft.offerIdsTotal ?? 0 }));
    },
  });

  const clear = useMutation({
    mutationFn: () => deleteExperimentOfferIds(experimentId ?? ''),
    onSuccess: (draft) => {
      applyResult(draft);
    },
  });

  return { upload, clear };
}

/**
 * Maps an offer-ids failure onto copy for the file field. The backend sends
 * `{ message, errors[] }`; `errors[0]` is the specific parse complaint (empty
 * file, header-only, an id over the 255-char column width) and is worth showing
 * verbatim rather than paraphrasing — it names the actual row.
 */
export function offerIdsErrorMessage(err: unknown, t: (key: string) => string): string {
  if (err instanceof ApiError) {
    if (err.statusCode === 409) return t('create.step2.upload.errors.locked');
    if (err.statusCode === 404) return t('create.step2.upload.errors.notFound');
    const first = err.messages.at(0);
    if (err.statusCode === 400 && first) return first;
    if (err.statusCode === 400) return t('create.step2.upload.errors.invalidFile');
  }
  return extractApiError(err, t('create.step2.upload.errors.generic'));
}

/**
 * Mutation for partial draft updates between wizard steps. Each step
 * commits the slice of state it owns (e.g. step 2 sends `scopingFilters` +
 * `stage`). Caller awaits the mutation before advancing so a failure keeps
 * the user on the current step.
 */
export function useUpdateExperimentDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      draftId,
      body,
      armAssignmentFile,
    }: {
      draftId: string;
      body: DraftExperimentUpdateBody;
      /** Optional arm-assignment CSV — when present the PATCH is sent multipart. */
      armAssignmentFile?: File;
    }) => updateExperimentDraft(draftId, body, armAssignmentFile),
    onSuccess: (updatedDraft, { draftId }) => {
      // Keep the cached draft in lockstep with the server so step components
      // that re-hydrate on entry (see `ProductGroupingScreen`) read the
      // latest persisted state without an extra round-trip.
      queryClient.setQueryData(queryKeys.experiments.draft(draftId), updatedDraft);
      // The review step derives its summary (segment name, skuCount, platform)
      // from the experiment-detail query, not the draft. Invalidate it so a
      // grouping/scope change re-fetches fresh — otherwise revisiting step 5
      // renders a stale summary from cache (e.g. segments after switching to
      // "No grouping"). Not mounted during steps 1–4, so this only refetches
      // once step 5 (or the detail page) reads it.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.experiments.detail(draftId),
      });
    },
    onError: (err) => {
      // 409 means the experiment has left Draft and is lifecycle-locked on the
      // backend — show an actionable message rather than the bare HTTP reason.
      if (err instanceof ApiError && err.statusCode === 409) {
        toast.error(
          extractApiError(
            err,
            "This experiment has already been launched and can't be edited here — start a new experiment to continue.",
          ),
        );
        return;
      }
      toast.error(extractApiError(err, 'Failed to save your progress. Please try again.'));
    },
  });
}

/**
 * Discard a draft from the listing page. On success invalidates the whole
 * experiments cache namespace so the row disappears from the table and any
 * dependent queries (stats, etc.) refresh. Surfaces a confirmation toast on
 * success and the standard error toast on failure.
 */
export function useDeleteExperimentDraft() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('experiments');
  return useMutation({
    mutationFn: (id: string) => deleteExperimentDraft(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.experiments.all });
      toast.success(t('discardDraft.success'));
    },
    onError: (err) => {
      toast.error(extractApiError(err, t('discardDraft.error')));
    },
  });
}

/**
 * Fetch a draft experiment by id — drives the resume flow's hydration.
 * `staleTime: 0` so a fresh resume always reads the latest server state;
 * the bigger catalog prereqs (kpis / content-attributes / etc.) keep their
 * own staleTime in their respective hooks.
 */
export function useExperimentDraft(id: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.experiments.draft(id ?? ''),
    queryFn: () => {
      if (!id) throw new Error('experiment id is required');
      return fetchExperimentDraft(id);
    },
    enabled: !!id,
    staleTime: 0,
    retry: 1,
  });
}
